import type { InitData } from '../core'
import { t } from '../core/i18n'

export type InlinePaymentMethod = 'mobilepay' | 'stripe'

/**
 * Har studiet BÅDE MobilePay og kort inline? Kun da er der et valg at vise —
 * ellers sender modalerne ingen metode og lader serveren vælge som før.
 * Uden vælgeren vandt MobilePay altid, og en kunde der ville betale med kort
 * kunne ikke gøre det inline.
 */
export function hasPaymentChoice(initData: InitData): boolean {
  const methods = initData.features?.inlinePaymentMethods ?? []
  return methods.includes('mobilepay') && methods.includes('stripe')
}

/** Samme integration, men en norsk kunde kender den som Vipps — som på den native flade. */
function mobilePayName(currency?: string): string {
  return currency?.toUpperCase() === 'NOK' ? 'Vipps' : 'MobilePay'
}

interface Props {
  initData: InitData
  value: InlinePaymentMethod
  onChange: (method: InlinePaymentMethod) => void
  disabled?: boolean
}

/**
 * Metode-vælger (MobilePay eller kort) til booking-, købs- og gavekort-modalen.
 * Vises kun når hasPaymentChoice() er sand; MobilePay er forvalgt (standardvalget
 * når studiet har begge). Valget sendes som eksplicit `paymentMethod`, som
 * widget-ruterne respekterer.
 */
export function PaymentMethodPicker({ initData, value, onChange, disabled }: Props) {
  const s = t(initData.locale)
  const options: Array<{ id: InlinePaymentMethod; label: string }> = [
    { id: 'mobilepay', label: mobilePayName(initData.currency) },
    { id: 'stripe', label: s.paymentMethodCard },
  ]
  return (
    <fieldset class="hsw-pay" disabled={disabled}>
      <style>{styles}</style>
      <legend class="hsw-pay-legend">{s.paymentMethod}</legend>
      <div class="hsw-pay-options">
        {options.map(o => (
          <label key={o.id} class={`hsw-pay-option${value === o.id ? ' hsw-pay-active' : ''}`}>
            <input
              type="radio"
              name="hsw-pay-method"
              value={o.id}
              checked={value === o.id}
              onChange={() => onChange(o.id)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

const styles = `
  .hsw-pay {
    border: 0;
    padding: 0;
    margin: 0 0 0.75rem 0;
    min-width: 0;
  }
  .hsw-pay-legend {
    padding: 0;
    margin-bottom: 0.4rem;
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-pay-options {
    display: flex;
    gap: 0.5rem;
  }
  .hsw-pay-option {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.65rem 0.85rem;
    border: 1px solid var(--yoga-card-border, var(--yoga-border, #e5e7eb));
    border-radius: var(--yoga-radius, 6px);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
  }
  .hsw-pay-option.hsw-pay-active {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: inset 0 0 0 1px var(--yoga-primary, #FF6B35);
  }
  .hsw-pay-option input {
    margin: 0;
    accent-color: var(--yoga-primary, #FF6B35);
  }
  .hsw-pay[disabled] .hsw-pay-option {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
